import React, { useState, useEffect } from "react";
import {
  Paper,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Button,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel"; // For the cross icon
import { Clear } from "@mui/icons-material";
import API from "../api";

const SearchBar = ({ searchHandler, locations, specialities }) => {
  const [searchFields, setSearchFields] = useState({
    specialty: "",
    location: "",
    city: "",
    employer: "",
  });
  const [cities, setCities] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch cities when location changes
  const getCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      const resp = await API.get(`/api/get-cities?status=1&state_id=${stateId}`);
      if (resp?.data?.success) {
        setCities(resp?.data?.data || []);
      }
    } catch (error) {
      console.log(error);
      setCities([]);
    }
  };

  useEffect(() => {
    if (searchFields.location) {
      getCities(searchFields.location);
    } else {
      setCities([]);
      setSearchFields((prev) => ({ ...prev, city: "" }));
    }
  }, [searchFields.location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // If location changes, clear city selection
    if (name === "location") {
      setSearchFields({
        ...searchFields,
        [name]: value,
        city: "",
      });
    } else {
      setSearchFields({
        ...searchFields,
        [name]: value,
      });
    }
  };

  const handleSearch = () => {
    if (searchFields?.location || searchFields?.specialty) {
      searchHandler(searchFields);
    }
  };

  const handleRemoveFilter = (filterName) => {
    setSearchFields((prevState) => {
      const updatedFields = {
        ...prevState,
        [filterName]: "",
      };
      searchHandler(updatedFields);
      return updatedFields;
    });
  };

  return (
    <>
      <Paper
        component="form"
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 653,
          bgcolor: "white",
          borderRadius: "10px",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          // spacing={isMobile ? 2 : 0}
          sx={{ width: "100%" }}
          divider={
            <Divider
              orientation={isMobile ? "horizontal" : "vertical"}
              flexItem
            />
          }
        >
          <FormControl sx={{ flex: 1, maxWidth: "90vw" }}>
            <Select
              name="specialty"
              value={searchFields?.specialty || ""}
              onChange={handleInputChange}
              displayEmpty
              inputProps={{ "aria-label": "Specialty" }}
              sx={{
                color: searchFields?.specialty === "" && "gray",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                height: { xs: "42px", sm: "45px", md: "auto" },
              }}
              IconComponent={searchFields?.specialty ? () => null : undefined}
              endAdornment={
                searchFields?.specialty && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleRemoveFilter("specialty")}
                      sx={{ padding: 0 }}
                      aria-label="Clear selection"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="">Select Specialty</MenuItem>
              {specialities?.map((specialty) => (
                <MenuItem
                  key={specialty?.id}
                  value={specialty?.id}
                  sx={{
                    fontSize: { xs: "12px", md: "14px" },
                  }}
                >
                  {specialty?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Location Select */}
          <FormControl sx={{ flex: 1, maxWidth: "90vw" }}>
            <Select
              name="location"
              value={searchFields.location}
              onChange={handleInputChange}
              displayEmpty
              inputProps={{ "aria-label": "Location" }}
              sx={{
                color: searchFields?.location === "" && "gray",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                height: { xs: "42px", sm: "45px", md: "auto" },
              }}
              IconComponent={searchFields?.location ? () => null : undefined}
              endAdornment={
                searchFields?.location && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleRemoveFilter("location")}
                      sx={{ padding: 0 }}
                      aria-label="Clear selection"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" sx={{ color: "gray" }}>
                Select Location
              </MenuItem>
              {locations?.map((location, index) => (
                <MenuItem
                  key={index}
                  value={location?.id}
                  sx={{ fontSize: { xs: "12px", sm: "45px", md: "14px" } }}
                >
                  {location?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* City Select */}
          <FormControl sx={{ flex: 1, maxWidth: "90vw" }}>
            <Select
              name="city"
              value={searchFields.city}
              onChange={handleInputChange}
              displayEmpty
              disabled={!searchFields.location || cities.length === 0}
              inputProps={{ "aria-label": "City" }}
              sx={{
                color: searchFields?.city === "" && "gray",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                height: { xs: "42px", sm: "45px", md: "auto" },
              }}
              IconComponent={searchFields?.city ? () => null : undefined}
              endAdornment={
                searchFields?.city && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleRemoveFilter("city")}
                      sx={{ padding: 0 }}
                      aria-label="Clear selection"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="" sx={{ color: "gray" }}>
                Select City
              </MenuItem>
              {cities?.map((city, index) => (
                <MenuItem
                  key={index}
                  value={city?.id}
                  sx={{ fontSize: { xs: "12px", sm: "45px", md: "14px" } }}
                >
                  {city?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Button */}
          <Button
            variant="contained"
            sx={{
              p: "10px",
              color: "white",
              bgcolor: "#E5B94E !important",
              "&:hover": { bgcolor: "#d4aa45 !important" },
              borderRadius: "0%",
              borderTopRightRadius: { md: "10px" },
              borderBottomRightRadius: "10px",
              borderBottomLeftRadius: { xs: "10px", md: "0px" },
              height: { xs: "42px", md: "auto" },
            }}
            onClick={handleSearch}
          >
            <Typography variant="body2" sx={{ display: { md: "none" }, py: 1 }}>
              Search
            </Typography>
            <SearchIcon
              sx={{ fontSize: "2rem", display: { xs: "none", md: "block" } }}
            />
          </Button>
        </Stack>
      </Paper>
    </>
  );
};

export default SearchBar;
